import React, { useCallback, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import { useRegister } from "../../../context/RegisterContext";
import { midiToNote, noteToMidi } from "../../../utils/registerVoicing";

interface RegisterKeyboardSectionProps {
  disabled?: boolean;
  activeNotes?: string[];
  isSequencerPlaying?: boolean;
}

// Range slider constants
const MIN_MIDI = 12; // C0
const MAX_MIDI = 108; // C8
const MIN_HEIGHT = 12; // Minimum 12 semitones (one octave)
const TOTAL_RANGE = MAX_MIDI - MIN_MIDI; // 96 semitones

const BLACK_PITCH_CLASSES = new Set([1, 3, 6, 8, 10]);

type DragType = "left" | "right" | "middle";

interface DragState {
  type: DragType;
  startX: number;
  startBottomMidi: number;
  startTopMidi: number;
}

interface PendingUpdate {
  type: DragType;
  bottomMidi: number;
  topMidi: number;
}

const RegisterKeyboardSection: React.FC<RegisterKeyboardSectionProps> = ({
  disabled = false,
  activeNotes = [],
  isSequencerPlaying = false,
}) => {
  const { register, setRegister, setBottomNote, setTopNote } = useRegister();

  // Drag state
  const [isDragging, setIsDragging] = useState(false);
  const dragStateRef = useRef<DragState | null>(null);
  const sliderRef = useRef<HTMLDivElement>(null);
  const rafIdRef = useRef<number | null>(null);
  const pendingUpdateRef = useRef<PendingUpdate | null>(null);
  const lastMidiRef = useRef<{
    bottomMidi: number | null;
    topMidi: number | null;
  }>({
    bottomMidi: null,
    topMidi: null,
  });
  const setRegisterRef = useRef(setRegister);
  const setBottomNoteRef = useRef(setBottomNote);
  const setTopNoteRef = useRef(setTopNote);

  React.useEffect(() => {
    setRegisterRef.current = setRegister;
    setBottomNoteRef.current = setBottomNote;
    setTopNoteRef.current = setTopNote;
  }, [setRegister, setBottomNote, setTopNote]);

  React.useEffect(() => {
    if (!register) {
      lastMidiRef.current = { bottomMidi: null, topMidi: null };
      return;
    }

    lastMidiRef.current = {
      bottomMidi: noteToMidi(register.bottomNote),
      topMidi: noteToMidi(register.topNote),
    };
  }, [register]);

  // Convert MIDI to percentage position on slider
  const midiToPercent = (midi: number): number => {
    return ((midi - MIN_MIDI) / TOTAL_RANGE) * 100;
  };

  // Convert percentage position to MIDI
  const percentToMidi = useCallback((percent: number): number => {
    const midi = Math.round((percent / 100) * TOTAL_RANGE + MIN_MIDI);
    return Math.max(MIN_MIDI, Math.min(MAX_MIDI, midi));
  }, []);

  // Get current slider positions
  const getSliderPositions = () => {
    if (!register) return { leftPercent: 0, rightPercent: 0 };
    const bottomMidi = noteToMidi(register.bottomNote);
    const topMidi = noteToMidi(register.topNote);
    return {
      leftPercent: midiToPercent(bottomMidi),
      rightPercent: midiToPercent(topMidi),
    };
  };

  const applyPendingUpdate = useCallback((update: PendingUpdate) => {
    if (update.type === "left") {
      if (lastMidiRef.current.bottomMidi === update.bottomMidi) {
        return;
      }

      lastMidiRef.current.bottomMidi = update.bottomMidi;
      setBottomNoteRef.current(midiToNote(update.bottomMidi));
      return;
    }

    if (update.type === "right") {
      if (lastMidiRef.current.topMidi === update.topMidi) {
        return;
      }

      lastMidiRef.current.topMidi = update.topMidi;
      setTopNoteRef.current(midiToNote(update.topMidi));
      return;
    }

    const isBottomSame = lastMidiRef.current.bottomMidi === update.bottomMidi;
    const isTopSame = lastMidiRef.current.topMidi === update.topMidi;
    if (isBottomSame && isTopSame) {
      return;
    }

    lastMidiRef.current.bottomMidi = update.bottomMidi;
    lastMidiRef.current.topMidi = update.topMidi;
    setRegisterRef.current({
      bottomNote: midiToNote(update.bottomMidi),
      topNote: midiToNote(update.topMidi),
    });
  }, []);

  const flushPendingUpdate = useCallback(() => {
    if (rafIdRef.current !== null) {
      cancelAnimationFrame(rafIdRef.current);
      rafIdRef.current = null;
    }

    const pendingUpdate = pendingUpdateRef.current;
    if (!pendingUpdate) {
      return;
    }

    pendingUpdateRef.current = null;
    applyPendingUpdate(pendingUpdate);
  }, [applyPendingUpdate]);

  const queueUpdate = useCallback(
    (update: PendingUpdate) => {
      pendingUpdateRef.current = update;

      if (rafIdRef.current !== null) {
        return;
      }

      rafIdRef.current = window.requestAnimationFrame(() => {
        rafIdRef.current = null;
        const pendingUpdate = pendingUpdateRef.current;
        if (!pendingUpdate) {
          return;
        }

        pendingUpdateRef.current = null;
        applyPendingUpdate(pendingUpdate);
      });
    },
    [applyPendingUpdate]
  );

  // Handle mouse down on slider
  const handleMouseDown = useCallback(
    (e: React.MouseEvent<HTMLDivElement>, type: DragType) => {
      if (disabled || !register || !sliderRef.current) return;

      e.preventDefault();
      const rect = sliderRef.current.getBoundingClientRect();
      const x = e.clientX - rect.left;

      const bottomMidi = noteToMidi(register.bottomNote);
      const topMidi = noteToMidi(register.topNote);

      if (rafIdRef.current !== null) {
        cancelAnimationFrame(rafIdRef.current);
        rafIdRef.current = null;
      }

      pendingUpdateRef.current = null;
      lastMidiRef.current = { bottomMidi, topMidi };
      dragStateRef.current = {
        type,
        startX: x,
        startBottomMidi: bottomMidi,
        startTopMidi: topMidi,
      };
      setIsDragging(true);
    },
    [disabled, register]
  );

  // Handle mouse move
  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      const dragState = dragStateRef.current;
      if (!dragState || !sliderRef.current) return;

      const rect = sliderRef.current.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const currentPercent = (x / rect.width) * 100;
      const startPercent = (dragState.startX / rect.width) * 100;
      const deltaMidi =
        percentToMidi(currentPercent) - percentToMidi(startPercent);

      if (dragState.type === "left") {
        // Dragging left handle - adjust bottom note
        const newBottomMidi = Math.max(
          MIN_MIDI,
          Math.min(
            dragState.startBottomMidi + deltaMidi,
            dragState.startTopMidi - MIN_HEIGHT
          )
        );
        queueUpdate({
          type: "left",
          bottomMidi: newBottomMidi,
          topMidi: dragState.startTopMidi,
        });
      } else if (dragState.type === "right") {
        // Dragging right handle - adjust top note
        const newTopMidi = Math.min(
          MAX_MIDI,
          Math.max(
            dragState.startTopMidi + deltaMidi,
            dragState.startBottomMidi + MIN_HEIGHT
          )
        );
        queueUpdate({
          type: "right",
          bottomMidi: dragState.startBottomMidi,
          topMidi: newTopMidi,
        });
      } else if (dragState.type === "middle") {
        // Dragging middle - shift entire register
        const height = dragState.startTopMidi - dragState.startBottomMidi;
        const newBottomMidi = Math.max(
          MIN_MIDI,
          Math.min(dragState.startBottomMidi + deltaMidi, MAX_MIDI - height)
        );
        const newTopMidi = newBottomMidi + height;
        if (newTopMidi <= MAX_MIDI) {
          queueUpdate({
            type: "middle",
            bottomMidi: newBottomMidi,
            topMidi: newTopMidi,
          });
        }
      }
    },
    [percentToMidi, queueUpdate]
  );

  // Handle mouse up
  const handleMouseUp = useCallback(() => {
    flushPendingUpdate();
    dragStateRef.current = null;
    setIsDragging(false);
  }, [flushPendingUpdate]);

  // Set up global mouse event listeners
  React.useEffect(() => {
    if (isDragging) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
      return () => {
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
      };
    }
  }, [handleMouseMove, handleMouseUp, isDragging]);

  React.useEffect(() => {
    return () => {
      if (rafIdRef.current !== null) {
        cancelAnimationFrame(rafIdRef.current);
      }
    };
  }, []);

  const sliderPositions = getSliderPositions();

  const keyboardData = useMemo(() => {
    let whiteIndex = 0;
    const keys = [];
    for (let midi = MIN_MIDI; midi <= MAX_MIDI; midi += 1) {
      const pitchClass = midi % 12;
      const isBlack = BLACK_PITCH_CLASSES.has(pitchClass);
      const noteName = midiToNote(midi);
      const isC = noteName.startsWith("C") && !noteName.includes("#");
      const label = isC ? noteName : "";

      if (!isBlack) {
        keys.push({
          midi,
          noteName,
          isBlack: false,
          whiteIndex,
          label,
        });
        whiteIndex += 1;
      } else {
        keys.push({
          midi,
          noteName,
          isBlack: true,
          whiteIndex: whiteIndex - 1,
          label: "",
        });
      }
    }

    const whiteKeys = keys.filter((key) => !key.isBlack);
    const blackKeys = keys.filter((key) => key.isBlack);

    return {
      whiteKeys,
      blackKeys,
      whiteKeyCount: whiteKeys.length,
    };
  }, []);

  const whiteKeyWidthPercent = 100 / keyboardData.whiteKeyCount;
  const blackKeyWidthPercent = whiteKeyWidthPercent * 0.62;

  const activeMidiSet = useMemo(() => {
    if (!isSequencerPlaying) {
      return new Set<number>();
    }

    const midiSet = new Set<number>();
    activeNotes.forEach((note) => {
      try {
        const midi = noteToMidi(note);
        if (midi >= MIN_MIDI && midi <= MAX_MIDI) {
          midiSet.add(midi);
        }
      } catch {
        return;
      }
    });
    return midiSet;
  }, [activeNotes, isSequencerPlaying]);

  const registerRange = useMemo(() => {
    if (!register) return null;
    return {
      bottom: noteToMidi(register.bottomNote),
      top: noteToMidi(register.topNote),
    };
  }, [register]);

  return (
    <RegisterKeyboardSectionWrapper id="register-keyboard-section">
      <KeyboardWrapper id="register-keyboard-wrapper">
        <KeyboardBackground id="register-keyboard">
          <RegisterControls id="register-controls">
            <RegisterSliderContainer
              id="register-slider-container"
              ref={sliderRef}
              onMouseDown={(e) => {
                if (!register || disabled) return;
                const target = e.target as HTMLElement;
                if (
                  target.id === "register-slider-handle-left" ||
                  target.id === "register-slider-handle-right"
                ) {
                  return;
                }
                handleMouseDown(e, "middle");
              }}
              disabled={disabled || !register}
            >
              <RegisterSliderTrack id="register-slider-track">
                <RegisterSliderRange
                  id="register-slider-range"
                  style={{
                    left: `${sliderPositions.leftPercent}%`,
                    width: `${
                      sliderPositions.rightPercent - sliderPositions.leftPercent
                    }%`,
                  }}
                  onMouseDown={(e) => {
                    e.stopPropagation();
                    handleMouseDown(e, "middle");
                  }}
                />
                <RegisterSliderHandle
                  id="register-slider-handle-left"
                  style={{ left: `${sliderPositions.leftPercent}%` }}
                  onMouseDown={(e) => {
                    e.stopPropagation();
                    handleMouseDown(e, "left");
                  }}
                  disabled={disabled || !register}
                />
                <RegisterSliderHandle
                  id="register-slider-handle-right"
                  style={{ left: `${sliderPositions.rightPercent}%` }}
                  onMouseDown={(e) => {
                    e.stopPropagation();
                    handleMouseDown(e, "right");
                  }}
                  disabled={disabled || !register}
                />
              </RegisterSliderTrack>
            </RegisterSliderContainer>
          </RegisterControls>
          <BlackKeysRow id="register-black-keys">
            {keyboardData.blackKeys.map((key) => {
              const leftPercent = (key.whiteIndex + 1) * whiteKeyWidthPercent;
              return (
                <BlackKey
                  id={`register-black-key-${key.midi}`}
                  key={key.midi}
                  $isActive={activeMidiSet.has(key.midi)}
                  $inRegister={
                    registerRange
                      ? key.midi >= registerRange.bottom &&
                        key.midi <= registerRange.top
                      : false
                  }
                  $widthPercent={blackKeyWidthPercent}
                  style={{
                    left: `calc(${leftPercent}% - ${
                      blackKeyWidthPercent / 2
                    }%)`,
                  }}
                />
              );
            })}
          </BlackKeysRow>
          <WhiteKeysRow
            id="register-white-keys"
            $whiteKeyCount={keyboardData.whiteKeyCount}
            $isActive={activeMidiSet.size > 0}
          >
            {keyboardData.whiteKeys.map((key) => (
              <WhiteKey
                id={`register-white-key-${key.midi}`}
                key={key.midi}
                $isActive={activeMidiSet.has(key.midi)}
                $inRegister={
                  registerRange
                    ? key.midi >= registerRange.bottom &&
                      key.midi <= registerRange.top
                    : false
                }
              >
                {key.label ? (
                  <KeyLabel id={`register-key-label-${key.midi}`}>
                    {key.label}
                  </KeyLabel>
                ) : null}
              </WhiteKey>
            ))}
          </WhiteKeysRow>
        </KeyboardBackground>
      </KeyboardWrapper>
    </RegisterKeyboardSectionWrapper>
  );
};

export default RegisterKeyboardSection;

const RegisterKeyboardSectionWrapper = styled.div.attrs({
  id: "register-keyboard-section-wrapper",
})`
  display: flex;
  flex-direction: column;
  gap: 14px;
  width: 100%;
  border-radius: 10px;
`;

const RegisterControls = styled.div.attrs({
  id: "register-controls-wrapper",
})`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 0px 0px 12px 0px;
`;

const RegisterSliderContainer = styled.div.attrs({
  id: "register-slider-container-wrapper",
})<{ disabled?: boolean }>`
  width: 100%;
  position: relative;
  padding: 8px 0;
  cursor: ${(props) => (props.disabled ? "not-allowed" : "pointer")};
  opacity: ${(props) => (props.disabled ? 0.5 : 1)};
`;

const RegisterSliderTrack = styled.div.attrs({
  id: "register-slider-track-wrapper",
})`
  width: 100%;
  height: 8px;
  border-radius: 12px;
  position: relative;
`;

const RegisterSliderRange = styled.div.attrs({
  id: "register-slider-range-wrapper",
})`
  position: absolute;
  height: 100%;
  background: #0c6196;
  border-radius: 4px;
  pointer-events: auto;
  cursor: move;
`;

const RegisterSliderHandle = styled.div.attrs({
  id: "register-slider-handle",
})<{ disabled?: boolean }>`
  position: absolute;
  top: 50%;
  transform: translate(-50%, -50%);
  width: 12px;
  height: 12px;
  background: #7bc6f4;
  border-radius: 50%;
  box-shadow: 0 0 6px rgba(123, 198, 244, 0.6);
  cursor: ${(props) => (props.disabled ? "not-allowed" : "ew-resize")};
  pointer-events: auto;
  z-index: 2;

  &:hover {
    transform: translate(-50%, -50%) scale(1.1);
  }
`;

const KeyboardWrapper = styled.div.attrs({
  id: "register-keyboard-wrapper-container",
})`
  width: 100%;
  display: flex;
  justify-content: center;
`;

const KeyboardBackground = styled.div.attrs({
  id: "register-keyboard-background",
})`
  width: 100%;
  background: #101010;
  border-radius: 0px 0px 10px 10px;
  border: 1px solid #2d2d2d;
  padding: 14px 14px 18px;
  position: relative;
  overflow: hidden;
`;

const BlackKeysRow = styled.div.attrs({
  id: "register-black-keys-row",
})`
  position: absolute;
  left: 14px;
  right: 14px;
  height: 27%;
  pointer-events: none;
  z-index: 3;
`;

const BlackKey = styled.div.attrs({
  id: "register-black-key",
})<{ $isActive: boolean; $inRegister: boolean; $widthPercent: number }>`
  position: absolute;
  top: 0;
  height: 100%;
  width: ${(props) => `${props.$widthPercent}%`};
  background: ${(props) =>
    props.$isActive ? "#7bc6f4" : props.$inRegister ? "#0C6196" : "#5a5a5a"};
  border-radius: 4px;
  border: 1px solid
    ${(props) =>
      props.$isActive ? "#7bc6f4" : props.$inRegister ? "#0C6196" : "#5a5a5a"};
  border-top: 0px;
  box-shadow: ${(props) =>
    props.$isActive
      ? "0 0 8px rgba(123, 198, 244, 0.55)"
      : "inset 0 -2px 4px rgba(0, 0, 0, 0.35)"};
`;

const WhiteKeysRow = styled.div.attrs({
  id: "register-white-keys-row",
})<{ $whiteKeyCount: number; $isActive: boolean }>`
  display: grid;
  grid-template-columns: repeat(${(props) => props.$whiteKeyCount}, 1fr);
  gap: 0;
  width: 100%;
  height: 72px;
  position: relative;
  z-index: 1;
  color: ${(props) => (props.$isActive ? "#000000" : "#ffffff")};
`;

const WhiteKey = styled.div.attrs({
  id: "register-white-key",
})<{ $isActive: boolean; $inRegister: boolean }>`
  position: relative;
  height: 100%;
  background: ${(props) => (props.$isActive ? "#7bc6f4" : "transparent")};
  border: 1px solid
    ${(props) =>
      props.$isActive ? "#7bc6f4" : props.$inRegister ? "#0C6196" : "#5a5a5a"};
  border-top: 0px;
  color: ${(props) => (props.$inRegister ? "#0C6196" : "#5a5a5a")};
  border-radius: 3px;
  box-shadow: ${(props) =>
    props.$isActive
      ? "0 0 10px rgba(123, 198, 244, 0.35)"
      : "inset 0 -6px 6px rgba(0, 0, 0, 0.25)"};
`;

const KeyLabel = styled.div.attrs({
  id: "register-key-label",
})`
  position: absolute;
  bottom: 6px;
  left: 3px;
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
`;
